import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Gift, 
  CreditCard, 
  Package, 
  DollarSign, 
  Shield, 
  ShieldAlert,
  Users,
  TrendingUp,
  Calendar,
  Link,
  Loader2,
  Clock,
  Gem,
  Bot,
  User,
  MessageCircle,
  Hash,
  Image,
  FileText,
  Sparkles,
  ShoppingCart,
  Swords,
  CircleDot,
  Banknote,
  Bitcoin
} from 'lucide-react'
import { transactionApi } from '../services'
import toast from 'react-hot-toast'

/**
 * Компонент страницы истории транзакций
 * Отображает историю всех операций пользователя с пагинацией
 */
const TransactionHistory = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  // Состояния компонента
  const [allTransactions, setAllTransactions] = useState([]) // Все транзакции с сервера
  const [displayedCount, setDisplayedCount] = useState(10) // Количество отображаемых транзакций
  const [isLoading, setIsLoading] = useState(true) // Загрузка данных
  const [error, setError] = useState(null) // Ошибка загрузки

  /**
   * Получение иконки для типа транзакции
   * @param {string} type - Тип транзакции
   * @returns {JSX.Element} - React компонент иконки
   */
  const getTransactionIcon = (type) => {
    const iconProps = { size: 20, className: "flex-shrink-0" }
    
    switch (type) {
      case '1': // Бонус за регистрацию
        return <Gift {...iconProps} className="text-accent-primary" />
      case '2': // Пополнение баланса
        return <CreditCard {...iconProps} className="text-blue-400" />
      case '3': // Открытие кейса
        return <Package {...iconProps} className="text-purple-400" />
      case '4': // Продажа предмета
        return <DollarSign {...iconProps} className="text-accent-primary" />
      case '5': // Зачисление администратором
        return <Shield {...iconProps} className="text-accent-primary" />
      case '6': // Списание администратором
        return <ShieldAlert {...iconProps} className="text-red-400" />
      case '7': // За регистрацию реферала
        return <Users {...iconProps} className="text-blue-400" />
      case '8': // Доход от реферала
        return <TrendingUp {...iconProps} className="text-accent-primary" />
      case '9': // Ежедневный бонус
        return <Calendar {...iconProps} className="text-yellow-400" />
      case '10': // За привязку Steam
        return <Link {...iconProps} className="text-blue-400" />
      case '11': // За привязку FaceIt
        return <Link {...iconProps} className="text-orange-400" />
      case '12': // Штраф за непринятый или отклоненный трейд
        return <ShieldAlert {...iconProps} className="text-red-400" />
      case '13': // Награда за бота в ФИО ТГ
        return <Bot {...iconProps} className="text-accent-primary" />
      case '14': // Награда за аватар в профиле ТГ
        return <Image {...iconProps} className="text-accent-primary" />
      case '15': // Награда за подписку на канал ТГ
        return <MessageCircle {...iconProps} className="text-blue-400" />
      case '16': // Награда за подписку на чат ТГ
        return <Hash {...iconProps} className="text-blue-400" />
      case '17': // Награда за ФИО в нике Steam
        return <User {...iconProps} className="text-accent-primary" />
      case '18': // Награда за аватар в Steam
        return <Image {...iconProps} className="text-blue-400" />
      case '19': // Бонус за реферальную ссылку в Telegram Bio
        return <FileText {...iconProps} className="text-accent-primary" />
      case '20': // Бонус за первое пополнение
        return <Sparkles {...iconProps} className="text-yellow-400" />
      case '21': // Покупка в магазине
        return <ShoppingCart {...iconProps} className="text-red-400" />
      case '22': // Создание кейсбатла
        return <Swords {...iconProps} className="text-purple-400" />
      case '23': // Возврат за кейсбатл
        return <Swords {...iconProps} className="text-yellow-400" />
      case '24': // Зачисление СБП
        return <Banknote {...iconProps} className="text-green-400" />
      case '25': // Зачисление crypto
        return <Bitcoin {...iconProps} className="text-yellow-400" />
      case '26': // Зачисление UZS
        return <CreditCard {...iconProps} className="text-green-400" />
      case '27': // Ставка в королевскую битву
        return <Swords {...iconProps} className="text-purple-400" />
      case '28': // Выигрыш в королевскую битву
        return <Swords {...iconProps} className="text-accent-primary" />
      case '29': // Возврат кристаллов за королевскую битву
        return <Swords {...iconProps} className="text-yellow-400" />
      case '30': // Пополнение картой РФ
        return <CreditCard {...iconProps} className="text-blue-400" />
      case '31': // Пополнение скинами Steam
        return <ShoppingCart {...iconProps} className="text-accent-primary" />
      case '32': // Пополнение KZT
        return <CreditCard {...iconProps} className="text-green-400" />
      case '33': // Пополнение Visa / MasterCard
        return <CreditCard {...iconProps} className="text-blue-400" />
      case '34': // Ставка в Double
        return <CircleDot {...iconProps} className="text-teal-400" />
      case '35': // Выигрыш в Double
        return <CircleDot {...iconProps} className="text-accent-primary" />
      case '36': // Возврат ставки Double
        return <CircleDot {...iconProps} className="text-yellow-400" />
      default:
        return <DollarSign {...iconProps} className="text-text-secondary" />
    }
  }

  /**
   * Получение цвета для значения транзакции
   * @param {string} value - Значение транзакции (например, "+500" или "-625")
   * @returns {string} - CSS класс для цвета
   */
  const getValueColor = (value) => {
    if (value.startsWith('+')) {
      return 'text-accent-primary'
    } else if (value.startsWith('-')) {
      return 'text-red-400'
    }
    return 'text-white'
  }

  /**
   * Загрузка всей истории транзакций с сервера
   */
  const loadTransactions = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Запрос к API - загружаем все транзакции сразу
      const response = await transactionApi.getTransactionHistory()
      
      if (response.history && Array.isArray(response.history)) {
        setAllTransactions(response.history)
      } else {
        setAllTransactions([])
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки истории транзакций:', error)
      setError(error.message || t('common.error'))
      toast.error(t('common.error'))
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Показать следующие 10 транзакций (клиентская пагинация)
   */
  const handleLoadMore = () => {
    setDisplayedCount(prev => prev + 10)
  }

  // Вычисляемые значения
  const displayedTransactions = allTransactions.slice(0, displayedCount)
  const hasMore = displayedCount < allTransactions.length

  /**
   * Возврат на страницу профиля
   */
  const handleGoBack = () => {
    navigate('/profile')
  }

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    loadTransactions()
  }, [])

  // Компонент отдельной транзакции
  const TransactionItem = ({ transaction }) => {
    const typeKey = transactionApi.getTransactionTypeKey(transaction.type)
    const timeAgo = transactionApi.formatTimeAgo(transaction.sec_ago, t)
    
    return (
      <div className="bg-surface rounded-lg p-4 border border-white border-opacity-10 hover:border-accent-primary/30 transition-all">
        <div className="flex items-center justify-between">
          {/* Левая часть - иконка и информация */}
          <div className="flex items-center gap-3 flex-1">
            {/* Иконка типа транзакции */}
            {getTransactionIcon(transaction.type)}
            
            {/* Информация о транзакции */}
            <div className="flex-1 min-w-0">
              {/* Название типа транзакции */}
              <span className="font-medium text-white">
                {t(`transactions.types.${typeKey}`)}
              </span>
              
              {/* Время и баланс */}
              <div className="text-text-secondary text-sm mt-1">
                <div>{timeAgo}</div>
                <div>{t('transactions.balance', { balance: parseInt(transaction.balance).toLocaleString() })}</div>
              </div>
            </div>
          </div>
          
          {/* Правая часть - сумма операции */}
          <div className="flex items-center gap-2">
            <span className={`font-medium ${getValueColor(transaction.value)}`}>
              {transaction.value}
            </span>
            <span className="text-accent-primary"><Gem size={20} /></span>
          </div>
        </div>
      </div>
    )
  }

  // Компонент загрузки (skeleton)
  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {[...Array(5)].map((_, index) => (
        <div key={index} className="bg-surface rounded-lg p-4 border border-white border-opacity-10 animate-pulse">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div className="w-5 h-5 bg-white bg-opacity-20 rounded mt-1"></div>
              <div className="flex-1">
                <div className="h-4 bg-white bg-opacity-20 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-white bg-opacity-10 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-white bg-opacity-10 rounded w-2/3"></div>
              </div>
            </div>
            <div className="text-right">
              <div className="h-4 bg-white bg-opacity-20 rounded w-16"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  // Основной рендер компонента
  return (
    <div className="min-h-screen bg-dark-primary text-white lg:max-w-2xl lg:mx-auto">
      {/* Header */}
      <div className="p-6 pb-0">
        <div className="flex items-center gap-4 mb-4 justify-center">
          <button 
            onClick={handleGoBack}
            className="p-2 rounded-lg bg-surface hover:bg-opacity-80 transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-accent-primary">
            {t('transactions.title')}
          </h1>
        </div>
      </div>

      {/* Основной контент */}
      <div className="p-6 space-y-6">
        {/* Состояние загрузки */}
        {isLoading && (
          <LoadingSkeleton />
        )}

        {/* Состояние ошибки */}
        {error && !isLoading && (
          <div className="bg-surface rounded-lg p-8 border border-white border-opacity-10 text-center">
            <div className="text-red-400 mb-4">
              <ShieldAlert size={48} className="mx-auto" />
            </div>
            <h3 className="text-white font-medium mb-2">{t('common.error')}</h3>
            <p className="text-text-secondary text-sm mb-4">{error}</p>
            <button
              onClick={() => loadTransactions()}
              className="bg-accent-primary hover:bg-accent-secondary text-dark-primary px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Пустое состояние */}
        {!isLoading && !error && allTransactions.length === 0 && (
          <div className="bg-surface rounded-lg p-8 border border-white border-opacity-10 text-center">
            <div className="text-accent-primary mb-4">
              <Clock size={48} className="mx-auto" />
            </div>
            <h3 className="text-white font-medium mb-2">{t('transactions.empty')}</h3>
            <p className="text-text-secondary text-sm">{t('transactions.emptyDescription')}</p>
          </div>
        )}

        {/* Список транзакций */}
        {!isLoading && !error && allTransactions.length > 0 && (
          <div className="space-y-4">
            {displayedTransactions.map((transaction, index) => (
              <TransactionItem 
                key={`${transaction.sec_ago}-${transaction.type}-${index}`} 
                transaction={transaction} 
              />
            ))}
          </div>
        )}

        {/* Кнопка "Показать следующие 10" */}
        {!isLoading && !error && allTransactions.length > 0 && hasMore && (
          <button
            onClick={handleLoadMore}
            className="w-full bg-surface rounded-lg p-4 border border-white border-opacity-10 transition-all hover:border-accent-primary/30"
          >
            <div className="flex items-center justify-center gap-3">
              <Clock size={20} className="text-accent-primary" />
              <span className="font-medium text-white">{t('transactions.loadMore')}</span>
            </div>
          </button>
        )}
      </div>
    </div>
  )
}

export default TransactionHistory
